/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { FC } from "react";
import { PCView } from "components/common/MediaQuery";
import { EventInfo, toDoubleDigits } from "utility";
import { EventVideo } from "@spwn/types/firebase/firestore";

import { StreamingStandbyOverlay } from "./StreamingStandbyOverlay";
import { StreamingStandbyToggleDarkmode } from "./StreamingStandbyToggleDarkmode";
import { StreamingStandbyCommentArea } from "./StreamingStandbyCommentArea";
import { StreamingStandbyEventTitle } from "./StreamingStandbyEventTitle";
import { EventDescription } from "features/events/EventDescription";
import { XShareButton } from "ui/XShareButton";
import { PublicPlanContentStreamingDTO } from "api/schema/spwn/dto/publicPlanContent";

export const StreamingStandby: FC<{
  planContent: PublicPlanContentStreamingDTO;
  displayEvent: EventInfo;
  eventVideoMap: {
    [videoId: string]: EventVideo;
  };
  isDarkModeEnabled: boolean;
}> = (props) => {
  return <StreamingStandbyLogic {...props} />;
};

const StreamingStandbyLogic: FC<{
  planContent: PublicPlanContentStreamingDTO;
  displayEvent: EventInfo;
  eventVideoMap: {
    [videoId: string]: EventVideo;
  };
  isDarkModeEnabled: boolean;
}> = (props) => {
  const currentVideo =
    props.eventVideoMap[props.planContent.details.streamingId];
  const thumbnail =
    // @ts-expect-error TS18048
    currentVideo.thumbnail.length === 0
      ? props.displayEvent.thumbnail
      : // @ts-expect-error TS18048
        currentVideo.thumbnail;
  // @ts-expect-error TS18048
  const videoTitle = currentVideo.name
    ? // @ts-expect-error TS18048
      `${props.displayEvent.eventTitle}【${currentVideo.name}】`
    : props.displayEvent.eventTitle;

  const [firstEventPart] = props.displayEvent.parts.slice(0, 1);
  // @ts-expect-error TS18048
  const dt = new Date(firstEventPart.startTime.seconds * 1000);
  const streamingStartAt = `${dt.getFullYear()}年${
    dt.getMonth() + 1
  }月${dt.getDate()}日 ${toDoubleDigits(dt.getHours())}:${toDoubleDigits(
    dt.getMinutes()
  )}`;

  return (
    <StreamingStandbyLayout
      isDarkModeEnabled={props.isDarkModeEnabled}
      StreamingStandbyOverlay={
        <StreamingStandbyOverlay
          thumbnail={thumbnail}
          videoTitle={videoTitle}
          streamingStatus={props.planContent.details.streamingStatus}
          startAt={streamingStartAt}
          // ブランドスラッグを取得する
          brandSlug={props.planContent.brand.slug}
        />
      }
      StreamingStandbyToggleDarkmode={
        <StreamingStandbyToggleDarkmode
          isDarkModeEnabled={props.isDarkModeEnabled}
        />
      }
      StreamingStandbyCommentArea={
        <StreamingStandbyCommentArea
          isDarkModeEnabled={props.isDarkModeEnabled}
        />
      }
      StreamingStandbyEventTitle={
        <StreamingStandbyEventTitle
          text={videoTitle}
          isDarkModeEnabled={props.isDarkModeEnabled}
        />
      }
      StreamingStandbyTwitterShareButton={
        <XShareButton
          eventId={props.displayEvent.eventId}
          eventTitle={props.displayEvent.eventTitle}
          hashTag={props.displayEvent.twitterHashTag}
        />
      }
      StreamingStandbyDescription={
        <EventDescription
          description={props.displayEvent.description}
          appearance={{
            isDarkModeEnabled: props.isDarkModeEnabled,
            isCompact: true,
          }}
        />
      }
    />
  );
};

const StreamingStandbyLayout: FC<{
  isDarkModeEnabled: boolean;
  StreamingStandbyOverlay: JSX.Element;
  StreamingStandbyToggleDarkmode: JSX.Element;
  StreamingStandbyCommentArea: JSX.Element;
  StreamingStandbyEventTitle: JSX.Element;
  StreamingStandbyTwitterShareButton: JSX.Element;
  StreamingStandbyDescription: JSX.Element;
}> = ({
  isDarkModeEnabled,
  StreamingStandbyOverlay,
  StreamingStandbyToggleDarkmode,
  StreamingStandbyCommentArea,
  StreamingStandbyEventTitle,
  StreamingStandbyTwitterShareButton,
  StreamingStandbyDescription,
}) => {
  return (
    <section
      css={css`
        padding-bottom: 120px;
        margin: 0 auto;
        background-color: ${isDarkModeEnabled ? "#000" : "#fff"};
        @media screen and (min-width: 768px) {
          width: 100%;
          max-width: 1800px;
          min-height: calc(100vh - 66px - 190px);
        }
      `}
    >
      <div
        id="Streaming"
        css={css`
          @media screen and (min-width: 768px) {
            display: grid;
            grid-template-columns: 1fr 360px;
            grid-gap: 20px 20px;
            width: 95%;
            margin: 0 auto;
            padding: 40px 0 0;
          }
        `}
      >
        <div>
          {/* 配信ステータスに応じた文言をオーバーレイで表示する */}
          {StreamingStandbyOverlay}

          {/* ダークモード切り替えボタンを表示する */}
          <PCView>
            <div
              css={css`
                margin-top: 8px;
              `}
            >
              {StreamingStandbyToggleDarkmode}
            </div>
          </PCView>
        </div>

        <PCView>{StreamingStandbyCommentArea}</PCView>
      </div>

      <div
        css={css`
          width: 95%;
          margin: 24px auto 0;
          @media screen and (min-width: 768px) {
            margin: 40px auto 0;
          }
        `}
      >
        <div
          css={css`
            padding-bottom: 20px;
            margin-bottom: 20px;
            border-bottom: 1px solid #e0e0e0;
            @media screen and (min-width: 768px) {
              padding-bottom: 32px;
            }
          `}
        >
          {StreamingStandbyEventTitle}
        </div>

        <div
          css={css`
            margin-bottom: 48px;
          `}
        >
          {StreamingStandbyTwitterShareButton}
        </div>

        {StreamingStandbyDescription}
      </div>
    </section>
  );
};
